// The tooth-editor DRAFT → the batch that goes on the wire.
//
// THE DRAFT MODEL: nothing is sent until «ثبت». A dentist charting a molar
// touches several surfaces in a row, and a request per tap would produce a
// burst of writes the server has to reconcile — and would make «انصراف»
// meaningless. Edits accumulate as a draft and go out as ONE batch, which is
// also exactly what the server's all-or-nothing RPC expects: one clinical act,
// one transaction.
//
// This lives in lib/ rather than inside the drawer because BOTH surfaces edit a
// tooth — the desktop `ToothDrawer` and the mobile `MobileToothSheet` — and
// they look nothing alike. What must NOT differ is the batch: the two forms
// emitting subtly different events for the same taps is precisely the class of
// bug §1.1 exists to prevent ("the two surfaces may look nothing alike but must
// never disagree"). Markup forks; this does not.
//
// It is a pure function of (draft, fdi, state) so it is testable without a DOM.

/**
 * @param draft { presence, condition:{value,material}, surfaces:{[code]:{value,material}}, note, isHistorical }
 * @param fdi   the tooth being edited
 * @param state the tooth's CURRENT server state (used only to drop a no-op presence)
 * @returns the ToothEventInput batch — empty when there is nothing to save
 */
export function buildToothEvents(draft, fdi, state) {
  if (!fdi) return [];
  const d = draft || {};
  const out = [];

  // A presence identical to what is already recorded is not an edit. Sending it
  // would append a second, redundant event to an append-only log.
  if (d.presence && d.presence !== state?.presence) {
    out.push({ toothFdi: fdi, aspect: 'presence', value: d.presence });
  }

  if (d.condition?.value) {
    out.push({
      toothFdi: fdi,
      aspect: 'condition',
      value: d.condition.value,
      material: d.condition.material || null,
    });
  }

  for (const [code, v] of Object.entries(d.surfaces || {})) {
    if (!v?.value) continue;
    out.push({
      toothFdi: fdi,
      aspect: 'surface',
      surface: code,
      value: v.value,
      material: v.material || null,
    });
  }

  if (d.note?.trim()) {
    out.push({ toothFdi: fdi, aspect: 'note', value: 'note', note: d.note.trim() });
  }

  // «کار قبلی» stamps the WHOLE batch: one visit to the editor is one clinical
  // statement, and half of it being "done today" while the other half is
  // historical is not a state a dentist can express or would mean.
  return out.map((e) => ({ ...e, isHistorical: !!d.isHistorical }));
}

/**
 * An AI image finding → the drawer's draft, ready for a dentist to confirm.
 *
 * DECIDED 2026-08-31: ticking a finding PRE-FILLS the editor; it does not
 * record. The alternative — writing the batch straight to the chart, the way
 * the narrative findings do — is faster for a mouth full of caries and wrong
 * for the same reason: a model that misread a tooth number puts a filling on
 * the wrong tooth, and the correction is a void entry in an append-only log
 * that a patient's chart carries forever. A pre-filled drawer costs one press
 * and makes the dentist the author.
 *
 * It seeds ONLY what the finding actually states. `note` is deliberately left
 * empty: the label is the model's sentence, and copying it into a clinical note
 * would file model text as the dentist's own words.
 *
 * `isHistorical` carries through, because "there is an old crown here" and
 * "I am placing a crown today" are different facts about the same tooth and
 * the model is often the one that can tell them apart from the film.
 */
export function seedFromFinding(finding) {
  const f = finding || {};
  const seed = { surfaces: {}, isHistorical: !!f.isHistorical };
  if (!f.toothFdi || !f.aspect || !f.value) return seed;

  if (f.aspect === 'presence') seed.presence = f.value;
  else if (f.aspect === 'condition') seed.condition = { value: f.value, material: null };
  else if (f.aspect === 'surface' && f.surface) {
    seed.surfaces[f.surface] = { value: f.value, material: null };
  }
  return seed;
}

export default buildToothEvents;
